import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../services/api/client.js';
import { metricErrorReason, metricReasonKey, normalizeMetrics, refreshOutcome } from '../../lib/socialMetrics.js';
import { ANALYTICS_CACHE_PREFIXES, notifyAnalyticsRefresh } from '../../lib/analyticsRefresh.js';
import { MetricGrid } from './MetricValue.jsx';
import TikTokStatisticsBanner from './TikTokStatisticsBanner.jsx';

export default function PersistedMetrics({ post, onReload }) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const pending = useRef(false);
  const alive = useRef(true);
  useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
  async function refresh() {
    if (pending.current) return;
    const scope = api.getReadScope();
    const current = () => alive.current && scope === api.getReadScope();
    pending.current = true;
    setBusy(true);
    setFeedback(null);
    let propagated = false;
    const propagateAnalytics = () => {
      if (propagated || !current()) return;
      api.invalidateCache?.(ANALYTICS_CACHE_PREFIXES);
      notifyAnalyticsRefresh(scope);
      propagated = true;
    };
    try {
      const result = await api.content.refreshMetrics(post.id, { limit: 5, intent: 'interactive' });
      if (!current()) return;
      const outcome = refreshOutcome(result);
      setFeedback({ key: outcome.refreshed
        ? outcome.complete ? 'refreshDone' : 'refreshPartial'
        : outcome.deferred ? 'refreshDeferred' : 'refreshUnavailable', retryAt: outcome.retryAt });
      if (outcome.refreshed) propagateAnalytics();
      // Reload attempt state as well as successful observations. A skip never
      // becomes a success toast, and retained field timestamps stay server-owned.
      try { await onReload(); } catch { /* The persisted outcome stays authoritative. */ }
    } catch (error) {
      if (!current()) return;
      const outcome = refreshOutcome(error);
      setFeedback({ key: outcome.refreshed ? 'refreshPartial' : 'refreshUnavailable', reason: metricErrorReason(error), failedAt: outcome.refreshed ? null : new Date().toISOString() });
      if (outcome.refreshed) {
        propagateAnalytics();
        try { await onReload(); } catch { /* Keep the persisted partial feedback. */ }
      }
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const metrics = normalizeMetrics(post.metrics, feedback?.failedAt
    ? { state: 'failed', reason: feedback.reason, lastAttemptedAt: feedback.failedAt } : post.metricFetch);
  const reason = metricReasonKey(feedback?.reason || post.metricFetch?.reason);
  const observedAt = Object.values(metrics.metricAvailability || {})
    .map(value => value?.measuredAt)
    .filter(value => Number.isFinite(Date.parse(value)))
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0]
    || metrics.legacyFetchedAt
    || metrics.fetchedAt
    || null;
  const reconnectTo = reason === 'permission' && post.platform && post.accountId
    ? `/app/settings?${new URLSearchParams({ tab: 'accounts', metrics: 'permission', platform: post.platform, account: post.accountId })}`
    : reason === 'authentication' && post.platform && post.accountId
      ? `/app/settings?${new URLSearchParams({ tab: 'accounts', reconnect: 'authentication', platform: post.platform, account: post.accountId })}`
      : '/app/settings?tab=accounts';
  const reconnectLink = ['authentication', 'permission'].includes(reason) ? <Link className="btn btn-ghost btn-sm" to={reconnectTo}>
    {t(reason === 'permission' && post.platform === 'tiktok' ? 'socialMetrics.enableTikTokStatistics' : 'postPreview.reconnect')}
  </Link> : null;
  return <section className="pp-section pp-performance" aria-label={t('postPreview.performance')} aria-busy={busy}>
    <div className="metric-actions">
      <h3 className="pp-section-h">{t('postPreview.performance')}</h3>
      <button type="button" className="btn btn-ghost btn-sm" disabled={busy} onClick={refresh}>{t(busy ? 'socialMetrics.refreshing' : 'postPreview.refresh')}</button>
    </div>
    {observedAt && <p className="metric-updated">{t('postPreview.latestObservation', { date: new Date(observedAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) })}</p>}
    {feedback && <p className="metric-feedback" role="status">{feedback.key === 'refreshDeferred' && feedback.retryAt
      ? t('socialMetrics.refreshDeferredUntil', { date: new Date(feedback.retryAt).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) })
      : t(`socialMetrics.${feedback.key}`)}{feedback.reason && ` ${t(`socialMetrics.${reason}`)}`}</p>}
    <MetricGrid metrics={metrics} platform={post.platform} compact />
    {post.platform === 'tiktok' && post.accountId
      ? <TikTokStatisticsBanner accountId={post.accountId} placement="post" fallback={reconnectLink} />
      : reconnectLink}
  </section>;
}
